import { Textarea, TextField } from '@ads/components-react';
import { ReceiptRefundIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IndividualPaymentParcelViewModel } from '@/shared/api/generated';
import { Alert } from '@/shared/ui/atoms';
import { ConfirmDialog } from '@/shared/ui/molecules';

import { REFUND_REASON_MAX_LENGTH, useParcelRefund } from '../../../hooks/useParcelRefund';
import { formatContractDate } from '../../../utils/formatContractDate';
import { EMPTY_VALUE, formatCurrency } from '../../../utils/formatCurrency';
import { formatCurrencyInput, parseCurrencyInput } from '../../../utils/maskedNumberInput';
import type { PlanWriteError } from '../../../utils/planWriteError';

/** Props de `ParcelRefundModal`. */
export interface ParcelRefundModalProps {
  /** Contrato da rota, para invalidar a grid no sucesso. */
  readonly financialPersonId: number | null;
  /** Parcela alvo. `null` mantém o diálogo fechado. */
  readonly parcelId: number | null;
  /** A parcela, de onde sai o valor pago que pré-preenche o campo. */
  readonly parcel?: IndividualPaymentParcelViewModel | undefined;
  /** Total de parcelas do contrato, para o rótulo `3/12`. */
  readonly numberParcels?: number | null | undefined;
  readonly onClose: () => void;
  /** Chamado depois de o backend aceitar a devolução. */
  readonly onRefunded: () => void;
}

/**
 * Diálogo de devolução do pagamento de uma parcela.
 *
 * É a operação "Reembolsar pagamento" (regra 11 do grid legado), sobre
 * `PATCH /pecegepay/creditcard/{id}/refund`.
 *
 * **Só existe o caso de cartão.** O diálogo legado tinha três estados por
 * `PaymentType`: bloco de cartão (3 e 7), bloco de boleto (1 e 6) e, em qualquer
 * outro valor, o Confirmar escondido com "reembolso não permitido". Boleto não
 * tem endpoint de devolução em `/api/v1`, então os outros dois estados não são
 * construíveis — e não precisam ser: `isParcelOperationAvailable` já restringe a
 * ação a 3 e 7, então quem chega aqui está sempre no ramo de cartão.
 *
 * Reproduz do bloco de cartão os dois campos que o endpoint aceita — **valor** e
 * **motivo** — e diz explicitamente o que os outros dois faziam e por que não
 * estão aqui:
 *
 * - o checkbox "Realizar estorno na Mundipagg/Pagar.me" é **implícito**: o
 *   endpoint é o do provedor, então não existe o caminho de só registrar a
 *   devolução localmente;
 * - a lista de e-mails de aviso **não tem parâmetro**, e o aviso não sai desta
 *   tela. Omitir o campo em silêncio deixaria o operador achar que avisou alguém.
 *
 * O valor vem pré-preenchido com o total pago, como no legado, e é editável para
 * permitir devolução parcial. Devolver **mais** do que foi pago é barrado aqui: o
 * backend recusaria, e a mensagem local diz qual é o teto.
 *
 * Erros não fecham o diálogo — a mensagem do backend aparece dentro dele e o
 * operador corrige o valor sem perder o motivo que digitou.
 */
export function ParcelRefundModal({
  financialPersonId,
  parcelId,
  parcel,
  numberParcels,
  onClose,
  onRefunded,
}: ParcelRefundModalProps) {
  const { t } = useTranslation('individualPayment');
  const paidValue = parcel?.totalPaid ?? null;

  // `undefined` distingue "ainda não tocou no campo" de "apagou o valor": no
  // primeiro caso o pré-preenchido do total pago vale, no segundo o campo fica
  // vazio de propósito e a confirmação é barrada.
  const [typedValue, setTypedValue] = useState<number | null | undefined>(undefined);
  const [reason, setReason] = useState('');
  const [failure, setFailure] = useState<PlanWriteError | null>(null);

  const { refund, isPending } = useParcelRefund(financialPersonId, parcelId);

  // Fechado é desmontado, como nos outros diálogos da tela: é o que descarta o
  // valor digitado e o erro da tentativa anterior sem um efeito de limpeza para
  // manter em sincronia.
  if (parcelId === null) return null;

  const value = typedValue === undefined ? paidValue : typedValue;
  const exceedsPaid = value !== null && paidValue !== null && value > paidValue;
  const isEmpty = value === null || value <= 0;

  const parcelLabel =
    parcel?.numberParcel == null
      ? EMPTY_VALUE
      : numberParcels == null
        ? t('parcels.table.parcelNumberShort', { number: parcel.numberParcel })
        : t('parcels.table.parcelNumber', { number: parcel.numberParcel, total: numberParcels });

  const handleConfirm = () => {
    if (isEmpty || exceedsPaid) return;
    setFailure(null);

    refund(
      { refundValue: value, refundReason: reason },
      {
        onSuccess: () => {
          setTypedValue(undefined);
          setReason('');
          onRefunded();
        },
        onError: setFailure,
      },
    );
  };

  return (
    <ConfirmDialog
      open
      tone="danger"
      icon={ReceiptRefundIcon}
      title={t('parcels.refund.title')}
      description={t('parcels.refund.question')}
      confirmLabel={t('parcels.refund.confirm')}
      cancelLabel={t('parcels.refund.cancel')}
      closeAriaLabel={t('parcels.refund.close')}
      isPending={isPending}
      confirmDisabled={isEmpty || exceedsPaid}
      onConfirm={handleConfirm}
      onClose={onClose}
    >
      <div className="flex flex-col gap-xxxs">
        <p className="ts-paragraph-xs text-ctx-content-placeholder">
          {t('parcels.refund.context', {
            parcel: parcelLabel,
            date: formatContractDate(parcel?.paymentDate),
            value: formatCurrency(paidValue),
          })}
        </p>

        {/* O que os dois campos ausentes do diálogo legado faziam. Fica sempre
            visível, não só em erro: é a diferença de comportamento que o operador
            precisa saber **antes** de confirmar. */}
        <Alert type="information" title={t('parcels.refund.scopeNotice')} />

        <TextField
          label={t('parcels.refund.valueLabel')}
          placeholder={t('parcels.refund.valuePlaceholder')}
          value={formatCurrencyInput(value)}
          onChange={event => setTypedValue(parseCurrencyInput(event.target.value))}
          hasError={exceedsPaid}
          helper={
            exceedsPaid
              ? t('parcels.refund.valueTooHigh', { value: formatCurrency(paidValue) })
              : t('parcels.refund.valueHelper')
          }
          disabled={isPending}
          fullWidth
        />

        <Textarea
          label={t('parcels.refund.reasonLabel')}
          optionalMessage={t('parcels.refund.optional')}
          placeholder={t('parcels.refund.reasonPlaceholder')}
          maxLength={REFUND_REASON_MAX_LENGTH}
          value={reason}
          onChange={event => setReason(event.target.value)}
          disabled={isPending}
          fullWidth
        />

        {failure === null ? null : (
          <Alert
            type="danger"
            title={
              failure.kind === 'server'
                ? t('parcels.refund.serverError')
                : failure.kind === 'forbidden'
                  ? t('parcels.refund.forbidden')
                  : failure.message === null
                    ? t('parcels.refund.rejectedFallback')
                    : t('parcels.refund.rejected', { message: failure.message })
            }
          />
        )}
      </div>
    </ConfirmDialog>
  );
}
